import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'horizontal' | 'featured';
}

export function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group block relative overflow-hidden rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300"
      >
        <div className="aspect-[16/10] overflow-hidden">
          <div className="w-full h-full relative">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <div className="flex items-center gap-2 mb-2">
            {article.isExclusive && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-accent-500 text-white rounded-full">
                <i className="ri-vip-crown-line text-xs"></i>独家
              </span>
            )}
            {article.isHot && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-primary-500 text-white rounded-full">
                <i className="ri-fire-line text-xs"></i>热门
              </span>
            )}
            <span className="text-xs text-white/80">{article.categoryName}</span>
          </div>
          <h3 className="text-lg font-heading font-bold text-white line-clamp-2 group-hover:text-primary-200 transition-colors">
            {article.title}
          </h3>
          <p className="mt-2 text-sm text-white/70 line-clamp-2">{article.excerpt}</p>
          <div className="flex items-center gap-4 mt-3 text-xs text-white/60">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>{(article.viewCount / 10000).toFixed(1)}万
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line"></i>{article.commentCount}
            </span>
            <span>{article.readTime}分钟阅读</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'horizontal') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex gap-4 p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300"
      >
        <div className="w-48 h-32 flex-shrink-0 overflow-hidden rounded-lg">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="flex-1 min-w-0 flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              {article.isExclusive && (
                <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xs font-medium bg-accent-100 text-accent-700 rounded">
                  <i className="ri-vip-crown-line text-xs"></i>独家
                </span>
              )}
              <span className="text-xs text-foreground-500">{article.categoryName}</span>
            </div>
            <h3 className="text-base font-heading font-bold text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors">
              {article.title}
            </h3>
            <p className="mt-1.5 text-sm text-foreground-600 line-clamp-2">{article.excerpt}</p>
          </div>
          <div className="flex items-center gap-4 text-xs text-foreground-500 mt-3">
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>{(article.viewCount / 10000).toFixed(1)}万
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line"></i>{article.commentCount}
            </span>
            <span>{article.readTime}分钟阅读</span>
            <span className="ml-auto">{new Date(article.publishedAt).toLocaleDateString('zh-CN')}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 overflow-hidden transition-all duration-300"
    >
      <div className="aspect-[16/10] overflow-hidden">
        <img
          src={article.coverImage}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          {article.isExclusive && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xs font-medium bg-accent-100 text-accent-700 rounded">
              <i className="ri-vip-crown-line text-xs"></i>独家
            </span>
          )}
          <span className="text-xs text-foreground-500">{article.categoryName}</span>
        </div>
        <h3 className="text-base font-heading font-bold text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors">
          {article.title}
        </h3>
        <p className="mt-2 text-sm text-foreground-600 line-clamp-2">{article.excerpt}</p>
        <div className="flex items-center gap-4 mt-3 text-xs text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>{(article.viewCount / 10000).toFixed(1)}万
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-chat-3-line"></i>{article.commentCount}
          </span>
          <span className="ml-auto">{new Date(article.publishedAt).toLocaleDateString('zh-CN')}</span>
        </div>
      </div>
    </Link>
  );
}